/**
 * Agent 配置中心页面（编排壳）：负责数据加载、状态与操作副作用，
 * 六维 + Agent 级维度面板委托至 components/configcenter/ 下各组件渲染。
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SlidersHorizontal, ArrowLeft, Rocket, CheckCircle, MessageSquare } from 'lucide-react';
import type { MarketplaceAsset } from '../types/marketplace';
import type { AssetRuntime, AssetRuntimeType } from '../types/assetRuntime';
import {
  getAgent,
  listAgentProfiles,
  getAgentProfile,
  updateProfilePersona,
  updateProfileAvatar,
  updateProfileVoice,
  activateProfile,
  cloneProfile,
  applyPersonaTemplate,
  listProfileSkills,
  listProfileWorkflows,
  listProfileMemories,
  listProfileTools,
  listGlobalTools,
  listPersonaTemplates,
  listAgentKnowledge,
  setProfileSkillEnabled,
  setProfileSkillTrust,
  setProfileWorkflowEnabled,
  deleteProfileMemory,
  attachProfileTool,
  detachProfileTool,
  routeSkillSearch as routeSkillSearchRequest,
  listAgentMcpServers,
  createAgentMcpServer,
  updateAgentMcpServer,
  deleteAgentMcpServer,
} from '../api/agents';
import { getCurrentUser } from '../api/users';
import { getAuthSession } from '../services/auth';
import {
  listAssetRuntimes,
  deployAssetRuntime,
  startAssetRuntime,
  stopAssetRuntime,
  restartAssetRuntime,
  deleteAssetRuntime,
  assetTypeLabel,
} from '../api/agentAssetRuntimes';
import {
  listAgentApiTokens,
  createAgentApiToken,
  updateAgentApiToken,
  deleteAgentApiToken,
  type AgentApiToken,
} from '../api/agentApiTokens';
import DimensionTabs from '../components/DimensionTabs';
import VersionSelector from '../components/VersionSelector';
import SkillEditor from '../components/SkillEditor';
import type { SkillItem as SkillEditorItem } from '../components/SkillEditor';
import WorkflowEditor from '../components/WorkflowEditor';
import type { WorkflowItem as WorkflowEditorItem } from '../components/WorkflowEditor';
import MemoryEditor from '../components/MemoryEditor';
import type { MemoryItem as MemoryEditorItem } from '../components/MemoryEditor';
import LlmConfigPanel from '../components/LlmConfigPanel';
import McpServerEditor, { type McpServer } from '../components/McpServerEditor';
import DeployWizard from '../components/DeployWizard';
import SchedulePanel from '../components/SchedulePanel';
import EvolutionPanel from '../components/EvolutionPanel';
import SkillMiningPanel from '../components/SkillMiningPanel';
import ConfigChangePanel from '../components/ConfigChangePanel';
import { getAgentDoctor } from '../api/doctor';
import type { DoctorReport } from '../api/doctor';
import { useToast } from '../components/ui/toastContext';
import { useConfirm } from '../components/ui/useConfirm';
import { DIMENSION_LABELS, MCP_PRESETS } from '../components/configcenter/constants';
import { getApiErrorMessage } from '../components/configcenter/utils';
import type {
  Agent,
  Profile,
  PersonaTemplate,
  GlobalTool,
  KnowledgeItem,
  SkillItem,
  WorkflowItem,
  MemoryItem,
  ToolItem,
} from '../components/configcenter/types';
import IdCopy from '../components/configcenter/IdCopy';
import AssetRuntimeToolbar from '../components/configcenter/AssetRuntimeToolbar';
import DoctorPanel from '../components/configcenter/DoctorPanel';
import ApiTokenPanel from '../components/configcenter/ApiTokenPanel';
import { useConfigCenterAssets } from '../components/configcenter/useConfigCenterAssets';
import PersonaPanel from '../components/configcenter/panels/PersonaPanel';
import SkillPanel from '../components/configcenter/panels/SkillPanel';
import WorkflowPanel from '../components/configcenter/panels/WorkflowPanel';
import MemoryPanel from '../components/configcenter/panels/MemoryPanel';
import ToolPanel from '../components/configcenter/panels/ToolPanel';
import McpPanel from '../components/configcenter/panels/McpPanel';
import KnowledgePanel from '../components/configcenter/panels/KnowledgePanel';
import DigitalHumanPanel from '../components/configcenter/panels/DigitalHumanPanel';

export default function AgentConfigCenter() {
  const params = useParams();
  // 路由参数按存在处理：缺省时保持与原模板字符串一致的 `/agents/undefined/...` 请求行为
  const agentId = params.agentId as string;
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [activeProfile, setActiveProfile] = useState<Profile | null>(null);
  const [selectedDimension, setSelectedDimension] = useState('人设');
  const [loading, setLoading] = useState(true);
  const [, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showWizard, setShowWizard] = useState(false);
  const [showConfigChange, setShowConfigChange] = useState(false);

  // Editor modal states
  const [editingSkill, setEditingSkill] = useState<SkillEditorItem | null | undefined>(undefined);
  const [editingWorkflow, setEditingWorkflow] = useState<WorkflowEditorItem | null | undefined>(undefined);
  const [editingMemory, setEditingMemory] = useState<MemoryEditorItem | null | undefined>(undefined);

  // Sub-resource lists
  const [skills, setSkills] = useState<SkillItem[]>([]);
  const [workflows, setWorkflows] = useState<WorkflowItem[]>([]);
  const [memories, setMemories] = useState<MemoryItem[]>([]);
  const [tools, setTools] = useState<ToolItem[]>([]);

  // Marketplace data
  const [globalTools, setGlobalTools] = useState<GlobalTool[]>([]);
  const [personaTemplates, setPersonaTemplates] = useState<PersonaTemplate[]>([]);
  const [knowledgeItems, setKnowledgeItems] = useState<KnowledgeItem[]>([]);
  const [mcpServers, setMcpServers] = useState<McpServer[]>([]);
  const [showMcpEditor, setShowMcpEditor] = useState(false);
  const [editingMcp, setEditingMcp] = useState<McpServer | null>(null);

  // Asset runtimes
  const [assetRuntimes, setAssetRuntimes] = useState<AssetRuntime[]>([]);
  const [assetRuntimeLoading, setAssetRuntimeLoading] = useState(false);

  // Doctor 体检报告（P5）：null=未加载/加载失败（面板诚实降级为不可用提示）
  const [doctorReport, setDoctorReport] = useState<DoctorReport | null>(null);
  const [doctorUnavailable, setDoctorUnavailable] = useState(false);

  // B17：当前用户是否具备技能信任档位管理权限（超管 / 当前租户 ADMIN）；
  // /auth/me 拉取失败诚实降级为 false（不展示「设为全局」，后端仍强制校验）
  const [canManageTrust, setCanManageTrust] = useState(false);

  // API tokens
  const [apiTokens, setApiTokens] = useState<AgentApiToken[]>([]);
  const [apiTokenLoading, setApiTokenLoading] = useState(false);
  const [showTokenForm, setShowTokenForm] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [newTokenSecret, setNewTokenSecret] = useState<string | null>(null);

  const loadAssetRuntimes = useCallback(async () => {
    if (!agentId) return;
    setAssetRuntimeLoading(true);
    try {
      const res = await listAssetRuntimes(agentId);
      setAssetRuntimes(res.runtimes);
    } catch (e) {
      console.error('Failed to load asset runtimes:', e);
    } finally {
      setAssetRuntimeLoading(false);
    }
  }, [agentId]);

  const loadDoctor = useCallback(async () => {
    if (!agentId) return;
    try {
      const report = await getAgentDoctor(agentId);
      setDoctorReport(report);
      setDoctorUnavailable(false);
    } catch (e) {
      console.warn('体检接口不可用:', e);
      setDoctorReport(null);
      setDoctorUnavailable(true);
    }
  }, [agentId]);

  // B17：解析当前用户信任管理权限（超管 / 当前租户 ADMIN），失败诚实降级为 false
  const loadTrustPermission = useCallback(async () => {
    try {
      const me = await getCurrentUser();
      const session = getAuthSession();
      const tenantRole = (me.tenants || []).find((t) => t.slug === session?.tenantSlug)?.role;
      setCanManageTrust(me.platform_role === 'super_admin' || tenantRole === 'ADMIN');
    } catch {
      setCanManageTrust(false);
    }
  }, []);

  // 体检 issue 锚点 → 配置中心维度 Tab / 运维页面
  const handleDoctorIssueClick = (anchor: string) => {
    const dimensionMap: Record<string, string> = {
      persona: '人设',
      skills: '技能',
      workflows: '工作流',
      knowledge: '知识',
      llm: 'LLM',
    };
    if (dimensionMap[anchor]) {
      setSelectedDimension(dimensionMap[anchor]);
    } else if (anchor === 'deployments') {
      navigate(`/agents/${agentId}/deployments`);
    } else if (anchor === 'runtime') {
      navigate('/instances');
    }
  };

  // 「说人话搜索」：调用本地 Skill 路由器按相关度排序；失败返回 null → 前端回退本地过滤
  const handleRouteSkillSearch = useCallback(async (term: string): Promise<string[] | null> => {
    if (!agentId) return null;
    try {
      return await routeSkillSearchRequest(agentId, term);
    } catch (e) {
      console.warn('技能路由推荐不可用，回退本地过滤:', e);
      return null;
    }
  }, [agentId]);

  const handleDeployAssetRuntime = async (assetType: AssetRuntimeType, assetId?: string) => {
    if (!activeProfile || !agentId) return;
    try {
      await deployAssetRuntime(agentId, {
        profile_id: activeProfile.id,
        asset_type: assetType,
        asset_id: assetId,
      });
      await loadAssetRuntimes();
      toast.success(`已部署 ${assetTypeLabel(assetType)} 在线服务`);
    } catch (e) {
      toast.error('部署失败: ' + getApiErrorMessage(e));
    }
  };

  const handleStartAssetRuntime = async (rt: AssetRuntime) => {
    if (!agentId) return;
    try {
      await startAssetRuntime(agentId, rt.id);
      await loadAssetRuntimes();
      toast.success('已启动');
    } catch (e) {
      toast.error('启动失败: ' + getApiErrorMessage(e));
    }
  };

  const handleStopAssetRuntime = async (rt: AssetRuntime) => {
    if (!agentId) return;
    try {
      await stopAssetRuntime(agentId, rt.id);
      await loadAssetRuntimes();
      toast.success('已停止');
    } catch (e) {
      toast.error('停止失败: ' + getApiErrorMessage(e));
    }
  };

  const handleRestartAssetRuntime = async (rt: AssetRuntime) => {
    if (!agentId) return;
    try {
      await restartAssetRuntime(agentId, rt.id);
      await loadAssetRuntimes();
      toast.success('已重启');
    } catch (e) {
      toast.error('重启失败: ' + getApiErrorMessage(e));
    }
  };

  const handleDeleteAssetRuntime = async (rt: AssetRuntime) => {
    if (!agentId) return;
    if (!(await confirm({ title: `确定删除 ${assetTypeLabel(rt.asset_type)} 运行时？`, danger: true }))) return;
    try {
      await deleteAssetRuntime(agentId, rt.id);
      await loadAssetRuntimes();
      toast.success('已删除');
    } catch (e) {
      toast.error('删除失败: ' + getApiErrorMessage(e));
    }
  };

  const loadApiTokens = useCallback(async () => {
    if (!agentId) return;
    setApiTokenLoading(true);
    try {
      const res = await listAgentApiTokens(agentId);
      setApiTokens(res);
    } catch (e) {
      console.error('Failed to load API tokens:', e);
      toast.error('加载 Token 失败: ' + getApiErrorMessage(e));
    } finally {
      setApiTokenLoading(false);
    }
  }, [agentId, toast]);

  const handleCreateApiToken = async () => {
    if (!agentId || !newTokenName.trim()) return;
    try {
      const res = await createAgentApiToken(agentId, { name: newTokenName.trim() });
      setNewTokenSecret(res.token);
      setNewTokenName('');
      await loadApiTokens();
      toast.success('Token 已生成，请立即复制');
    } catch (e) {
      toast.error('生成失败: ' + getApiErrorMessage(e));
    }
  };

  const handleToggleApiToken = async (token: AgentApiToken) => {
    if (!agentId) return;
    try {
      await updateAgentApiToken(agentId, token.id, { is_active: !token.is_active });
      await loadApiTokens();
      toast.success(token.is_active ? '已禁用' : '已启用');
    } catch (e) {
      toast.error('更新失败: ' + getApiErrorMessage(e));
    }
  };

  const handleDeleteApiToken = async (token: AgentApiToken) => {
    if (!agentId) return;
    if (!(await confirm({ title: '确定删除该 Token？', message: '使用此 Token 的调用将立即失效。', danger: true }))) return;
    try {
      await deleteAgentApiToken(agentId, token.id);
      await loadApiTokens();
      toast.success('已删除');
    } catch (e) {
      toast.error('删除失败: ' + getApiErrorMessage(e));
    }
  };

  const loadAgent = useCallback(async () => {
    try {
      const detail = await getAgent(agentId);
      setAgent(detail);
    } catch (e) {
      console.error('Failed to load agent:', e);
    }
  }, [agentId]);

  const loadProfiles = useCallback(async () => {
    try {
      const list = await listAgentProfiles(agentId);
      setProfiles(list);
      const active = list.find((p) => p.status === 'active');
      const target = active || list[0];
      if (target) setActiveProfile(target);
    } catch (e) {
      console.error('Failed to load profiles:', e);
    }
  }, [agentId]);

  const loadSubResources = useCallback(async (profileId: string) => {
    try {
      const [profile, skills, workflows, memories, tools, globalTools, templates, knowledge] = await Promise.all([
        getAgentProfile(profileId),
        listProfileSkills(profileId),
        listProfileWorkflows(profileId),
        listProfileMemories(profileId),
        listProfileTools(profileId),
        listGlobalTools(),
        listPersonaTemplates(),
        listAgentKnowledge(agentId),
      ]);
      setSkills(skills);
      setWorkflows(workflows);
      setMemories(memories);
      setTools(tools);
      setGlobalTools(globalTools);
      setPersonaTemplates(templates);
      setKnowledgeItems(knowledge);
      const mcpList = (profile.extra?.mcp_servers || []) as McpServer[];
      // 问题 #12：优先从实体 API 读取（mcp_servers 表），实体为空时回退 legacy extra
      let mcpEntities: McpServer[] = [];
      try {
        mcpEntities = (await listAgentMcpServers(agentId)) as McpServer[];
      } catch {
        mcpEntities = [];
      }
      const mcpSource = mcpEntities.length > 0 ? mcpEntities : mcpList;
      setMcpServers(mcpSource.map((s: McpServer, idx: number) => ({ ...s, id: s.id || `mcp-${idx}` })));
    } catch (e) {
      console.error('Failed to load sub-resources:', e);
    }
  }, [agentId]);

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState 闭包
    const tick = () => {
      if (agentId) {
        void Promise.all([loadAgent(), loadProfiles()]).finally(() => setLoading(false));
        void loadDoctor();
      }
    };
    tick();
  }, [agentId, loadAgent, loadProfiles, loadDoctor]);

  useEffect(() => {
    // B17：信任权限 /auth/me 解析（失败降级为 false，不影响主流程）
    const tick = () => {
      void loadTrustPermission();
    };
    tick();
  }, [loadTrustPermission]);

  useEffect(() => {
    // 同上：本地包装触发异步加载
    const tick = () => {
      if (activeProfile) {
        void loadSubResources(activeProfile.id);
        void loadAssetRuntimes();
      }
    };
    tick();
  }, [activeProfile, loadSubResources, loadAssetRuntimes]);

  useEffect(() => {
    // 同上：本地包装触发异步加载
    const tick = () => {
      if (selectedDimension === 'API Token') {
        void loadApiTokens();
      }
    };
    tick();
  }, [selectedDimension, loadApiTokens]);

  const handleSavePersona = async (data: object) => {
    if (!activeProfile) return;
    setSaving(true);
    try {
      await updateProfilePersona(activeProfile.id, data);
      await loadProfiles();
      toast.success('人设配置已保存');
    } catch {
      setError('保存失败');
      toast.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAvatar = async (data: object) => {
    if (!activeProfile) return;
    setSaving(true);
    try {
      await updateProfileAvatar(activeProfile.id, data);
      await loadProfiles();
      toast.success('形象配置已保存');
    } catch {
      setError('保存失败');
      toast.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveVoice = async (data: object) => {
    if (!activeProfile) return;
    setSaving(true);
    try {
      await updateProfileVoice(activeProfile.id, data);
      await loadProfiles();
      toast.success('声音配置已保存');
    } catch {
      setError('保存失败');
      toast.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (profileId: string) => {
    setActivating(true);
    try {
      await activateProfile(profileId);
      await loadProfiles();
      await loadAgent();
      toast.success('Profile 已激活');
    } catch {
      setError('激活失败');
      toast.error('激活失败');
    } finally {
      setActivating(false);
    }
  };

  const handleDeploy = () => {
    setShowWizard(true);
  };

  const handleDeleteMemory = async (memoryId: string) => {
    if (!(await confirm({ title: '确定删除此记忆策略？', danger: true }))) return;
    try {
      await deleteProfileMemory(activeProfile!.id, memoryId);
      await loadSubResources(activeProfile!.id);
      toast.success('记忆策略已删除');
    } catch (e) {
      toast.error('删除失败: ' + getApiErrorMessage(e));
    }
  };

  const handleClone = async (sourceId: string) => {
    const version = prompt('新版本号:', '1.1.0');
    if (!version) return;
    try {
      await cloneProfile(sourceId, version);
      await loadProfiles();
      toast.success('版本已克隆');
    } catch (e) {
      setError('克隆失败');
      toast.error('克隆失败: ' + getApiErrorMessage(e));
    }
  };

  const handleApplyPersonaTemplate = async (template: PersonaTemplate) => {
    if (!agent || !agent.active_profile_id) {
      toast.error('Agent 没有激活的 Profile');
      return;
    }
    setSaving(true);
    try {
      await applyPersonaTemplate(agent.id, template.id);
      await loadProfiles();
      toast.success(`已应用人设模板：${template.name}`);
    } catch (e) {
      toast.error('应用失败: ' + getApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSkill = async (asset: MarketplaceAsset) => {
    if (!activeProfile) return;
    try {
      await setProfileSkillEnabled(activeProfile.id, asset.id, !asset.isEnabled);
      await loadSubResources(activeProfile.id);
      toast.success(asset.isEnabled ? '技能已禁用' : '技能已启用');
    } catch (e) {
      toast.error('更新失败: ' + getApiErrorMessage(e));
    }
  };

  const handleSetSkillGlobal = async (asset: MarketplaceAsset) => {
    // B17：人工提升到高信任档位（后端按 OWNER/ADMIN 校验，越权 403）
    if (!activeProfile) return;
    try {
      await setProfileSkillTrust(activeProfile.id, asset.id, 'global');
      await loadSubResources(activeProfile.id);
      toast.success(`技能「${asset.displayName}」已设为全局（可跨 Agent 推荐复用）`);
    } catch (e) {
      toast.error('操作失败: ' + getApiErrorMessage(e));
    }
  };

  const handleToggleWorkflow = async (asset: MarketplaceAsset) => {
    if (!activeProfile) return;
    try {
      await setProfileWorkflowEnabled(activeProfile.id, asset.id, !asset.isEnabled);
      await loadSubResources(activeProfile.id);
      toast.success(asset.isEnabled ? '工作流已禁用' : '工作流已启用');
    } catch (e) {
      toast.error('更新失败: ' + getApiErrorMessage(e));
    }
  };

  const handleToggleTool = async (asset: MarketplaceAsset) => {
    if (!activeProfile) return;
    try {
      if (asset.isApplied) {
        const attached = tools.find((t) => t.tool_id === asset.id);
        if (attached) {
          await detachProfileTool(activeProfile.id, attached.id);
        }
      } else {
        await attachProfileTool(activeProfile.id, asset.id);
      }
      await loadSubResources(activeProfile.id);
      toast.success(asset.isApplied ? '工具已解绑' : '工具已挂载');
    } catch (e) {
      toast.error('操作失败: ' + getApiErrorMessage(e));
    }
  };

  const handleUpdateMcpServers = async (nextServers: McpServer[]) => {
    if (!agentId) return;
    try {
      // 问题 #12：MCP Server 一等实体 —— 走 /agents/{id}/mcp-servers CRUD 同步，
      // 后端写操作会自动同步回 active profile 的 extra.mcp_servers（legacy 兼容）
      const normalized = nextServers.map((s, idx) => ({ ...s, id: s.id || `mcp-${idx}` }));
      const existing = await listAgentMcpServers(agentId);
      const existingIds = new Set(existing.map((s) => s.id));
      const nextIds = new Set(normalized.map((s) => s.id));

      // 新增（本地 mcp-* 前缀）与更新（已入库 uuid）
      for (const s of normalized) {
        if (existingIds.has(s.id)) {
          await updateAgentMcpServer(agentId, s.id, {
            name: s.name,
            description: s.description,
            command: s.command ?? undefined,
            args: s.args || [],
            env: s.env || {},
            transport: s.transport,
            url: s.url ?? undefined,
            headers: s.headers,
            token: s.token,
          });
        } else {
          await createAgentMcpServer(agentId, {
            name: s.name,
            description: s.description,
            command: s.command ?? undefined,
            args: s.args || [],
            env: s.env || {},
            transport: s.transport,
            url: s.url ?? undefined,
            headers: s.headers,
            token: s.token,
          });
        }
      }
      // 删除：实体存在但已从列表移除
      for (const s of existing) {
        if (!nextIds.has(s.id)) {
          await deleteAgentMcpServer(agentId, s.id);
        }
      }
      setMcpServers(normalized);
      toast.success('MCP 工具已更新');
    } catch (e) {
      toast.error('保存失败: ' + getApiErrorMessage(e));
    }
  };

  const handleToggleMcp = async (asset: MarketplaceAsset) => {
    if (asset.isApplied) {
      const next = mcpServers.filter((s) => s.id !== asset.id);
      await handleUpdateMcpServers(next);
    } else {
      const preset = MCP_PRESETS.find((p) => p.id === asset.id);
      const next = [...mcpServers, preset || (asset.payload as McpServer)];
      await handleUpdateMcpServers(next);
    }
  };

  const handleSaveMcp = async (server: McpServer) => {
    const exists = mcpServers.some((s) => s.id === server.id);
    const next = exists
      ? mcpServers.map((s) => (s.id === server.id ? server : s))
      : [...mcpServers, server];
    await handleUpdateMcpServers(next);
  };

  const { personaAssets, skillAssets, workflowAssets, toolAssets, knowledgeAssets, mcpAssets } = useConfigCenterAssets({
    personaTemplates,
    skills,
    workflows,
    tools,
    globalTools,
    knowledgeItems,
    mcpServers,
  });

  const runtimeToolbar = (assetType: AssetRuntimeType) => (
    <AssetRuntimeToolbar
      assetType={assetType}
      runtimes={assetRuntimes}
      loading={assetRuntimeLoading}
      onDeploy={handleDeployAssetRuntime}
      onStart={handleStartAssetRuntime}
      onStop={handleStopAssetRuntime}
      onRestart={handleRestartAssetRuntime}
      onDelete={handleDeleteAssetRuntime}
    />
  );

  const renderDimensionPanel = () => {
    if (selectedDimension === 'API Token') {
      return (
        <ApiTokenPanel
          agent={agent}
          tokens={apiTokens}
          loading={apiTokenLoading}
          formOpen={showTokenForm}
          onOpenForm={() => {
            setShowTokenForm(true);
            setNewTokenSecret(null);
            setNewTokenName('');
          }}
          onCancelForm={() => setShowTokenForm(false)}
          newTokenName={newTokenName}
          onNewTokenNameChange={setNewTokenName}
          newTokenSecret={newTokenSecret}
          onCreate={handleCreateApiToken}
          onToggle={handleToggleApiToken}
          onDelete={handleDeleteApiToken}
        />
      );
    }
    if (selectedDimension === '定时任务') {
      // 定时任务是 Agent 级（不随 Profile 版本走），与 API Token 一样无需 activeProfile
      return agentId ? <SchedulePanel agentId={agentId} /> : null;
    }
    if (selectedDimension === '进化') {
      // 进化运行是 Agent 级（P12），面板内部校验 active Profile
      return agentId ? <EvolutionPanel agentId={agentId} /> : null;
    }
    if (selectedDimension === '沉淀') {
      // 离线沉淀（B5+B6）：Agent 级，不随 Profile 版本走
      return agentId ? <SkillMiningPanel agentId={agentId} /> : null;
    }
    if (!activeProfile) return <div>请先选择一个 Profile</div>;
    switch (selectedDimension) {
      case '人设':
        return (
          <PersonaPanel
            personaConfig={activeProfile.persona_config}
            assets={personaAssets}
            onSave={handleSavePersona}
            onToggle={(asset) => {
              const template = personaTemplates.find((t) => t.id === asset.id);
              if (template) handleApplyPersonaTemplate(template);
            }}
            toolbar={runtimeToolbar('persona')}
          />
        );
      case '技能':
        return (
          <SkillPanel
            assets={skillAssets}
            onToggle={handleToggleSkill}
            onSearchRoute={handleRouteSkillSearch}
            onSetGlobal={canManageTrust ? handleSetSkillGlobal : undefined}
            onEdit={(asset) => {
              const skill = skills.find((s) => s.id === asset.id);
              if (skill) setEditingSkill(skill);
            }}
            onAdd={() => setEditingSkill(null)}
            toolbar={runtimeToolbar('skill')}
          />
        );
      case '工作流':
        return (
          <WorkflowPanel
            assets={workflowAssets}
            onToggle={handleToggleWorkflow}
            onEdit={(asset) => {
              const wf = workflows.find((w) => w.id === asset.id);
              if (wf) setEditingWorkflow(wf);
            }}
            onAdd={() => setEditingWorkflow(null)}
            toolbar={runtimeToolbar('workflow')}
          />
        );
      case '记忆':
        return (
          <MemoryPanel
            memories={memories}
            onEdit={(m) => setEditingMemory(m)}
            onDelete={handleDeleteMemory}
            onAdd={() => setEditingMemory(null)}
          />
        );
      case '工具':
        return <ToolPanel assets={toolAssets} onToggle={handleToggleTool} />;
      case 'MCP 工具':
        return (
          <McpPanel
            assets={mcpAssets}
            onToggle={handleToggleMcp}
            onEdit={(asset) => {
              const server = mcpServers.find((s) => s.id === asset.id);
              if (server) { setEditingMcp(server); setShowMcpEditor(true); }
            }}
            onAdd={() => { setEditingMcp(null); setShowMcpEditor(true); }}
            toolbar={runtimeToolbar('mcp')}
          />
        );
      case '知识':
        return (
          <KnowledgePanel
            assets={knowledgeAssets}
            count={knowledgeItems.length}
            onManage={() => navigate(`/agents/${agentId}/teaching/knowledge`)}
            toolbar={runtimeToolbar('knowledge')}
          />
        );
      case 'LLM':
        return (
          <LlmConfigPanel
            profileId={activeProfile.id}
            llmConfigId={activeProfile.llm_config_id}
            onConfigChange={() => loadProfiles()}
          />
        );
      case '数字人':
        return (
          <DigitalHumanPanel
            avatarConfig={activeProfile.avatar_config}
            voiceConfig={activeProfile.voice_config}
            onSaveAvatar={handleSaveAvatar}
            onSaveVoice={handleSaveVoice}
          />
        );
      default:
        return <div>未知维度</div>;
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading"><div className="spinner" /></div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="container">
        <div className="empty"><h3>Agent 不存在</h3></div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="flex items-center justify-between mb-4" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <button className="secondary" onClick={() => navigate(`/agents/${agentId}`)}>
            <ArrowLeft size={16} /> 返回概览
          </button>
          <h1 style={{ margin: '12px 0 4px', wordBreak: 'break-word' }}>{agent.name} — <SlidersHorizontal size={20} style={{ verticalAlign: 'middle', marginRight: 6 }} />配置中心</h1>
          <div className="flex items-center gap-2" style={{ margin: '4px 0' }}>
            <IdCopy id={agent.id} label="Agent ID" />
            {agent.capability_id && (
              <span
                className="badge info"
                data-testid="capability-instance-badge"
                title={`该 Agent 是能力定义的实例（版本 ${agent.capability_version || '未知'}）；克隆副本从能力快照重建，不含知识库与凭证`}
              >
                能力实例
              </span>
            )}
          </div>
          <p className="text-muted">{agent.description || '暂无描述'}</p>
        </div>
        <div className="flex gap-2">
          <button className="secondary" onClick={() => setShowConfigChange(true)}>
            <MessageSquare size={14} /> 对话修改
          </button>
          <button className="primary" onClick={handleDeploy}>
            <Rocket size={14} /> 部署
          </button>
        </div>
      </div>

      {error && (
        <div className="card mb-4" style={{ borderColor: 'var(--color-error)', background: 'rgba(248,81,73,0.1)' }}>
          <p style={{ color: 'var(--color-error)', margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Doctor 体检面板（P5）：接口失败时降级为「不可用」提示，不阻塞页面 */}
      <DoctorPanel
        report={doctorReport}
        unavailable={doctorUnavailable}
        onReload={loadDoctor}
        onIssueClick={handleDoctorIssueClick}
      />

      <div className="grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <VersionSelector
            versions={profiles.map((p) => ({ id: p.id, version: p.version, status: p.status, created_at: p.created_at }))}
            activeId={activeProfile?.id || null}
            onSelect={(id) => setActiveProfile(profiles.find((p) => p.id === id) || null)}
            onClone={handleClone}
          />
          {activeProfile && (
            <div className="mt-4">
              <button
                className="primary w-full"
                onClick={() => handleActivate(activeProfile.id)}
                disabled={activating || activeProfile.status === 'active'}
              >
                {activating ? '激活中...' : <><CheckCircle size={14} /> 激活此版本</>}
              </button>
            </div>
          )}
        </div>

        <div className="col-span-3">
          <DimensionTabs active={selectedDimension} onChange={setSelectedDimension} dimensions={DIMENSION_LABELS} />
          <div className="card mt-4">
            {renderDimensionPanel()}
          </div>
        </div>
      </div>

      {/* Skill Editor Modal */}
      {editingSkill !== undefined && activeProfile && (
        <SkillEditor
          skill={editingSkill || undefined}
          profileId={activeProfile.id}
          onSave={() => {
            setEditingSkill(undefined);
            loadSubResources(activeProfile.id);
            toast.success(editingSkill ? '技能已更新' : '技能已创建');
          }}
          onCancel={() => setEditingSkill(undefined)}
        />
      )}

      {/* Workflow Editor Modal */}
      {editingWorkflow !== undefined && activeProfile && (
        <WorkflowEditor
          workflow={editingWorkflow || undefined}
          profileId={activeProfile.id}
          onSave={() => {
            setEditingWorkflow(undefined);
            loadSubResources(activeProfile.id);
            toast.success(editingWorkflow ? '工作流已更新' : '工作流已创建');
          }}
          onCancel={() => setEditingWorkflow(undefined)}
        />
      )}

      {/* Memory Editor Modal */}
      {editingMemory !== undefined && activeProfile && (
        <MemoryEditor
          memory={editingMemory || undefined}
          profileId={activeProfile.id}
          onSave={() => {
            setEditingMemory(undefined);
            loadSubResources(activeProfile.id);
            toast.success(editingMemory ? '记忆策略已更新' : '记忆策略已创建');
          }}
          onCancel={() => setEditingMemory(undefined)}
        />
      )}

      {/* MCP Server Editor Modal */}
      {showMcpEditor && activeProfile && (
        <McpServerEditor
          server={editingMcp || undefined}
          agentId={agentId}
          onSave={(server) => {
            handleSaveMcp(server);
            setShowMcpEditor(false);
            setEditingMcp(null);
          }}
          onCancel={() => {
            setShowMcpEditor(false);
            setEditingMcp(null);
          }}
        />
      )}

      {/* Deploy Wizard */}
      {showWizard && agent && activeProfile && (
        <DeployWizard
          agentId={agent.id}
          profileId={activeProfile.id}
          profileSummary={{
            version: activeProfile.version,
            status: activeProfile.status,
            skillCount: skills.length,
            workflowCount: workflows.length,
            toolCount: tools.length,
            memoryCount: memories.length,
            llmModel: activeProfile.llm_config_id ? '已配置' : '未配置',
            personaConfig: activeProfile.persona_config,
          }}
          onDeploy={() => {
            setShowWizard(false);
            navigate(`/agents/${agentId}/deployments`);
          }}
          onCancel={() => setShowWizard(false)}
        />
      )}

      {/* 对话修改（B1a）：自然语言 → 配置变更提案 → 逐项确认应用 */}
      {showConfigChange && agent && (
        <ConfigChangePanel
          agentId={agent.id}
          open
          onClose={() => setShowConfigChange(false)}
          onApplied={() => {
            loadProfiles();
            if (activeProfile) loadSubResources(activeProfile.id);
          }}
        />
      )}

      {dialog}
    </div>
  );
}
